/**
 * ShapeMediaServer — SSR-safe static variant of ShapeMedia.
 *
 * Renders a static clip-path without any client-side JavaScript or hydration.
 * Use this in Next.js Server Components or any SSR context where you don't
 * need morphing animation.
 *
 * @example
 * // In a Next.js Server Component
 * import { ShapeMediaServer } from "@bug-on/m3-expressive";
 *
 * export default function ProfileCard() {
 *   return (
 *     <ShapeMediaServer shape="sunny" width={200} height={200}>
 *       <img src="/avatar.jpg" alt="Profile" />
 *     </ShapeMediaServer>
 *   );
 * }
 */

import type { CSSProperties, ReactNode } from "react";
import { resolveShape } from "../../shapes/catalog/md3-expressive-shapes";
import { toClipPath } from "../../shapes/render/shape-rendering";
import type { ShapeRef } from "./types";

interface ShapeMediaServerProps {
	/** The shape to render. */
	shape: ShapeRef;
	/** Width in pixels. */
	width: number;
	/** Height in pixels. */
	height: number;
	/** Children to render inside the clipped area. */
	children?: ReactNode;
	/** Additional CSS class names. */
	className?: string;
	/** Inline styles. */
	style?: CSSProperties;
	/** ARIA label. */
	"aria-label"?: string;
	/** ARIA role. */
	role?: string;
}

/**
 * SSR-safe shape clip-path wrapper. No animations, no JS required.
 */
export function ShapeMediaServer({
	shape,
	width,
	height,
	children,
	className,
	style,
	"aria-label": ariaLabel,
	role,
}: ShapeMediaServerProps): React.JSX.Element {
	const polygon = resolveShape(shape);
	const clipPath = toClipPath(polygon, width, height);
	const resolvedRole = role ?? (ariaLabel ? "img" : "presentation");
	const accessibilityProps = ariaLabel
		? { role: resolvedRole, "aria-label": ariaLabel }
		: { role: resolvedRole };

	const computedStyle: CSSProperties = {
		...style,
		clipPath,
		overflow: "hidden",
		position: "relative",
	};

	return (
		<div className={className} style={computedStyle} {...accessibilityProps}>
			{children}
		</div>
	);
}
